import type { State } from 'src/store/types.js';
import type { TrashList, TrashPageViewModel } from 'src/pages/trash-page/trash-page-view-model.js';
import { displayName } from 'src/utilities/text';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { assetsDeleted } from '../../store/selectors/assets/assets-deleted';
import { substancesDeleted } from '../../store/selectors/assets/substances-deleted';
import { constitutionalDocumentsDeleted } from '../../store/selectors/documents/constitutional-documents-deleted';
import { contractsDeleted } from '../../store/selectors/documents/contracts-deleted';
import { documentsDeleted } from '../../store/selectors/documents/documents-deleted';
import { guidelinesDeleted } from '../../store/selectors/documents/guidelines-deleted';
import { reportsDeleted } from '../../store/selectors/documents/reports-deleted';
import { employeesDeleted } from '../../store/selectors/employees/employees-deleted';
import { issuesDeleted } from '../../store/selectors/issues/issues-deleted';
import { meetingsDeleted } from '../../store/selectors/meetings/meetings-deleted';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import { partnersDeleted } from '../../store/selectors/partners/partners-deleted';
import { contactsDeleted } from '../../store/selectors/partners/contacts-deleted';
import { tasksDeleted } from '../../store/selectors/responsibilities/tasks-deleted';
import { functionsDeletedWithTaskCount } from '../../store/selectors/responsibilities/functions-deleted-with-task-count';
import { riskAssessmentsDeleted } from '../../store/selectors/risk-assessments/risk-assessments-deleted';

function addTrashList(
  lists: TrashList[],
  items: {
    uuid: string;
    name?: string;
    accessControl?: string[];
    classification?: string;
    taskCount?: number;
    uploadStatus?: string;
  }[],
  icon: string,
  field: string,
  type: string,
) {
  if (items.length > 0) {
    lists.push({
      icon,
      field,
      type,
      items: items.map((item) => {
        let extraInfo = '';
        if (type === 'substances') {
          extraInfo = item.uploadStatus === 'UNREADABLE' ? 'Ikke lesbart' : '';
        } else if (item.taskCount !== undefined) {
          extraInfo = `(${item.taskCount} oppgaver)`;
        }

        return {
          uuid: item.uuid,
          name: displayName(item.name),
          accessControl: item.accessControl ?? [],
          classification: item.classification ?? 'NONE',
          extraInfo: extraInfo,
        };
      }),
    });
  }
}

/**
 * Lists the deleted entities.
 * Computer related items are not included since they are not shown.
 *
 * @param viewModel
 * @param state
 */
export function trashPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): TrashPageViewModel {
  const lists: TrashList[] = [];

  addTrashList(
    lists,
    constitutionalDocumentsDeleted(state),
    'constitutionalDocuments',
    'organization_constitutionalDocuments',
    'constitutionalDocuments',
  );
  addTrashList(lists, employeesDeleted(state), 'employees', 'organization_employees', 'employees');
  addTrashList(lists, partnersDeleted(state), 'partners', 'organization_partners', 'partners');
  addTrashList(lists, contactsDeleted(state), 'employees', 'organization_contacts', 'contacts');
  addTrashList(lists, functionsDeletedWithTaskCount(state), 'functions', 'organization_functions', 'functions');
  addTrashList(lists, tasksDeleted(state), 'tasks', 'organization_tasks', 'tasks');
  addTrashList(lists, meetingsDeleted(state), 'meetings', 'organization_meetings', 'meetings');
  addTrashList(lists, documentsDeleted(state), 'documents', 'organization_documents', 'documents');
  addTrashList(lists, guidelinesDeleted(state), 'guidelines', 'organization_guidelines', 'guidelines');
  addTrashList(lists, reportsDeleted(state), 'reports', 'organization_reports', 'reports');
  addTrashList(lists, contractsDeleted(state), 'contracts', 'organization_contracts', 'contracts');
  addTrashList(lists, assetsDeleted(state), 'assets', 'organization_assets', 'assets');
  addTrashList(lists, substancesDeleted(state), 'substances', 'organization_substances', 'substances');
  addTrashList(lists, issuesDeleted(state), 'issues', 'organization_issues', 'issues');
  addTrashList(
    lists,
    riskAssessmentsDeleted(state),
    'riskAssessments',
    'organization_riskAssessments',
    'riskAssessments',
  );

  return {
    ...viewModel,
    type: 'trash-page',
    icon: 'trash',
    href: hrefPrefix,
    lists: lists,
    organization: getOrganization(state),
  };
}
